import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ProductsService } from '../../../core/services/products.service';
import { ProductFamily } from '../../../core/models/product.model';
import { PopupInfoService } from '../../../shared/services/popup-info.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBox, faPercent, faArrowLeft, faLayerGroup } from '@fortawesome/pro-solid-svg-icons';

@Component({
  selector: 'app-products-form-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FontAwesomeModule],
  templateUrl: './products-form-page.html',
  styleUrls: ['./products-form-page.scss'],
})
export class ProductsFormPage implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly productsService = inject(ProductsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly popupInfoService = inject(PopupInfoService);

  // Icons
  faBox = faBox;
  faPercent = faPercent;
  faArrowLeft = faArrowLeft;
  faLayerGroup = faLayerGroup;

  productForm: FormGroup;
  productId: number | null = null;
  productFamilies: ProductFamily[] = [];
  isEditMode = false;
  isLoading = false;
  error: string | null = null;

  constructor() {
    this.productForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      category: ['', [Validators.required, Validators.maxLength(255)]],
      description: ['', [Validators.required]],
      estimated_value: ['', [Validators.required, Validators.maxLength(255)]],
      commission: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
      product_family_id: [null],
    });
  }

  get f() {
    return this.productForm.controls;
  }

  ngOnInit(): void {
    this.loadProductFamilies();
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.productId = Number(idParam);
      this.isEditMode = true;
      this.loadProduct(this.productId);
    }
  }

  loadProductFamilies(): void {
    this.productsService.listFamilies().subscribe({
      next: (families) => {
        this.productFamilies = families;
      },
      error: (err) => {
        console.error('Error loading product families:', err);
      },
    });
  }

  loadProduct(id: number): void {
    this.isLoading = true;
    this.productsService.getById(id).subscribe({
      next: (product) => {
        this.productForm.patchValue({
          name: product.name,
          category: product.category,
          description: product.description,
          estimated_value: product.estimated_value,
          commission: product.commission,
          product_family_id: product.product_family_id,
        });
        this.isLoading = false;
      },
      error: (err) => {
        this.error = 'Error al cargar el producto';
        this.popupInfoService.showError('Error al cargar el producto');
        this.isLoading = false;
        console.error('Error loading product:', err);
      },
    });
  }

  onSubmit(): void {
    if (this.productForm.invalid) {
      this.productForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.error = null;

    const productData = this.productForm.value;

    const request =
      this.isEditMode && this.productId
        ? this.productsService.update(this.productId, productData)
        : this.productsService.create(productData);

    request.subscribe({
      next: () => {
        this.popupInfoService.showSuccess(
          this.isEditMode ? 'Producto actualizado correctamente' : 'Producto creado correctamente'
        );
        this.router.navigate(['/admin/products']);
      },
      error: (err) => {
        const errorMessage = this.isEditMode
          ? 'Error al actualizar el producto'
          : 'Error al crear el producto';
        this.error = errorMessage;
        this.popupInfoService.showError(errorMessage);
        this.isLoading = false;
        console.error('Error saving product:', err);
      },
    });
  }

  onCancel(): void {
    this.router.navigate(['/admin/products']);
  }
}
